// File: src/pages/InMatchPage.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMatchFlow } from './useMatchFlow';
import EndMatchBottomSheet from './EndMatchBottomSheet';
import EventProgressNavBar from './EventProgressNavBar';
import { useToaster } from '../../context/ToasterContext';
import { enforceSpeedmatchState, bindRoutePulseEnforcer } from '../../utils/speedmatchEnforcer';
import MinimalFooter from '../MinimalFooter';
/* ────────────────────────────────────────── */
/* Mixer-style Timer Header (inline version)  */
function TimerHeader({
  timer = '00:00',
  subheading = 'Keep your answers short, honest, and to the point—then pass it back',
}) {
  const parts = String(timer).split(':');
  const hasHours = parts.length === 3;
  const [h, m, s] = hasHours ? parts : ['--', parts[0] ?? '--', parts[1] ?? '--'];

  return (
    <div style={TH.container}>
      <h1 style={TH.title}>
        Match is{' '}
        <span style={TH.highlight}>
          <span style={TH.highlightText}>Live</span>
        </span>
      </h1>

      <div style={TH.subheading}>{subheading}</div>

      <div style={TH.timerWrapper}>
        <div style={TH.timerLabelPill}>Timer Ends In</div>
        <div style={TH.digitsRow} aria-live="polite">
          {hasHours && (
            <>
              <span style={TH.digit}>{String(h).padStart(2, '0')[0]}</span>
              <span style={TH.digit}>{String(h).padStart(2, '0')[1]}</span>
              <span style={TH.colon}>:</span>
            </>
          )}
          <span style={TH.digit}>{String(m).padStart(2, '0')[0]}</span>
          <span style={TH.digit}>{String(m).padStart(2, '0')[1]}</span>
          <span style={TH.colon}>:</span>
          <span style={TH.digit}>{String(s).padStart(2, '0')[0]}</span>
          <span style={TH.digit}>{String(s).padStart(2, '0')[1]}</span>
        </div>
      </div>
    </div>
  );
}

const TH = {
  container: {
    boxSizing: 'border-box',
    maxWidth: 680,
    width: '80%',
    margin: '12px auto 0',
    padding: '0 12px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    textAlign: 'center',
    color: '#0b0b0b',
  },
  title: {
    margin: 0,
    fontSize: '1.85rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  highlight: {
    display: 'inline-block',
    padding: '3px 10px',
    paddingBottom: 4,
    borderRadius: 6,
    backgroundColor: '#3579F6',
    color: '#fff',
    transform: 'skewX(-8deg) rotate(-2deg)',
  },
  highlightText: {
    display: 'inline-block',
    transform: 'skewX(8deg) rotate(2deg)',
  },
  subheading: {
    marginTop: 6,
    fontSize: '.9rem',
    color: '#6b7280',
    fontWeight: 500,
    lineHeight: 1.45,
  },
  timerWrapper: {
    marginTop: 18,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  timerLabelPill: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background: '#fff',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    fontSize: '.7rem',
    color: '#374151',
    fontWeight: 800,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  digitsRow: { display: 'flex', alignItems: 'baseline', gap: 4 },
  digit: {
    display: 'inline-block',
    fontSize: '2.5rem',
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#0f172a',
    fontVariantNumeric: 'tabular-nums',
    marginTop: 8,
  },
  colon: {
    fontSize: '2rem',
    fontWeight: 900,
    color: '#9ca3af',
    lineHeight: 1,
    padding: '8px 4px',
    transform: 'translateY(-5px)',
  },
};

/* ────────────────────────────────────────── */
/* Mixer-style Icebreaker Card (inline)       */
function IcebreakerCard({ question = '...' }) {
  return (
    <div style={IC.wrapper}>
      <div style={IC.card}>
        <span style={IC.pill}>Icebreaker</span>
        <h2 style={IC.question}>{question}</h2>
      </div>
      <p style={IC.footer}>
        When the timer reaches zero you'll automatically return to your portal page — make the most
        of this chat!
      </p>
    </div>
  );
}

const IC = {
  wrapper: {
    width: '100%',
    maxWidth: 600,
    margin: '0 auto',
    fontFamily:
      "'Open Sans', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    textAlign: 'center',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  card: {
    position: 'relative',
    padding: '7.5rem 1.5rem 3.25rem',
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#ddd',
    boxShadow: '0 4px 50px rgba(165,165,165,0.08)',
    margin: '1rem',
    minHeight: 260,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    background: '#fff',
  },
  pill: {
    position: 'absolute',
    top: 80,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#555',
    fontWeight: 700,
    padding: '8px 16px',
    fontSize: '.8rem',
    background: '#eee',
    borderRadius: 100,
  },
  question: {
    fontSize: '1.75rem',
    fontWeight: 550,
    lineHeight: 1.5,
    letterSpacing: '-0.02em',
    textRendering: 'optimizeLegibility',
    margin: '0 auto',
    maxWidth: 360,
    color: '#111',
    marginTop: '.75rem',
    marginBottom: '1.75rem',
  },
  footer: {
    fontSize: '.75rem',
    lineHeight: 1.5,
    color: '#9ca3af',
    maxWidth: 300,
    margin: '0.25rem auto 0',
  },
};

/* ────────────────────────────────────────── */
      <MinimalFooter />



const MF = {
  footer: {
    padding: '12px 20px',
    backgroundColor: '#fff',
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '0.75rem',
    color: '#555',
  },
  line: {
    height: 1,
    backgroundColor: '#eee',
    marginBottom: 12,
    marginLeft: -20,
    marginRight: -20,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  copy: { whiteSpace: 'nowrap' },
  icons: { display: 'flex', gap: 12 },
  iconLink: { color: '#555', textDecoration: 'none', fontWeight: 700 },
};

/* ────────────────────────────────────────── */
/* In-Match Page (state-enforced refresh)     */
export default function InMatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToaster();

  const { match: currentMatch, endSession } = useMatchFlow();

  const userId = localStorage.getItem('userId') || null;
  const sessionId = localStorage.getItem('sessionId') || null;
  const api = process.env.REACT_APP_API_URL;
  const eventId =
    (typeof window !== 'undefined' && localStorage.getItem('mobileEventId')) || null;

  const [showEndSheet, setShowEndSheet] = useState(false);

  // local timer state (UI)
  const [timeLeft, setTimeLeft] = useState(0);

  // guards to avoid duplicate actions
  const redirectedRef = useRef(false);
  const endedViaTimerRef = useRef(false);

  // rehydrate + enforce gates
  const LAST_MATCH_KEY = 'sm:lastFoundMatchId';
  const [rehydrateTried, setRehydrateTried] = useState(false);
  const enforcerCheckedRef = useRef(false);

  // optional: stageEndsAt from enforcer navigation state to smooth refresh
  const stageEndsAt = location.state?.stageEndsAt || null;

  function authHeaders() {
    const token =
      localStorage.getItem('accessToken') ||
      JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
      '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  const incomingMatchId = location.state?.matchId || null;

  /* ───────── Rehydrate FIRST on hard refresh ───────── */
  useEffect(() => {
    if (currentMatch?._id) {
      setRehydrateTried(true);
      return;
    }
    const seedId = incomingMatchId || sessionStorage.getItem(LAST_MATCH_KEY);
    if (!seedId) {
      setRehydrateTried(true);
      return;
    }
    (async () => {
      try {
        await fetch(`${api}/matches/${seedId}`, { headers: authHeaders() })
          .then((r) => r.json())
          .catch(() => ({}));
        // We don't strictly need to setMatch here; enforcer will reconcile after.
      } catch {}
      setRehydrateTried(true);
    })();
  }, [api, currentMatch?._id, incomingMatchId]);

  // Keep LAST_MATCH_KEY updated when match exists
  useEffect(() => {
    if (!currentMatch?._id) return;
    try {
      sessionStorage.setItem(LAST_MATCH_KEY, String(currentMatch._id));
    } catch {}
  }, [currentMatch?._id]);

  /* ───────── Enforce ONLY AFTER rehydrate ───────── */
  // Enforce after rehydrate + bind to cron pulse
useEffect(() => {
  if (!eventId || !rehydrateTried) return;

  let alive = true;

  (async () => {
    await enforceSpeedmatchState({ api, eventId, pageKind: 'inmatch', navigate });
    if (alive) enforcerCheckedRef.current = true;
  })();

  // 🔑 Re-run on every route_state pulse from the server cron
  const cleanup = bindRoutePulseEnforcer({
    api,
    eventId,
    pageKind: 'inmatch',
    navigate,
  });

  return () => {
    alive = false;
    cleanup();
  };
}, [api, eventId, navigate, rehydrateTried]);

// Re-enforce on focus (nice to have)
useEffect(() => {
  if (!eventId) return;
  const onFocus = () => {
    enforceSpeedmatchState({ api, eventId, pageKind: 'inmatch', navigate });
  };
  window.addEventListener('focus', onFocus);
  return () => window.removeEventListener('focus', onFocus);
}, [api, eventId, navigate]);


  /* ───────── Don't bounce until both gates have run ───────── */
  useEffect(() => {
    if (!rehydrateTried || !enforcerCheckedRef.current) return;
    if (!currentMatch || !currentMatch._id) {
      const eId = localStorage.getItem('mobileEventId');
      navigate(eId ? `/matchflow?eventId=${eId}` : '/matchflow', { replace: true });
    }
  }, [currentMatch, navigate, rehydrateTried]);

  /* ───────── Seed sticky inmatch (helps enforcer demotion debounce) ───────── */
  useEffect(() => {
    if (!eventId) return;
    const endMs = currentMatch?.endTime ? new Date(currentMatch.endTime).getTime() : null;
    if (endMs && Number.isFinite(endMs)) {
      try {
        sessionStorage.setItem(`speedmatch_inmatch_until:${eventId}`, String(endMs));
      } catch {}
    }
  }, [eventId, currentMatch?.endTime]);

  /* ───────── Tick timer & auto end when zero ───────── */
  useEffect(() => {
    // Allow UI to display countdown even before match doc hydrates by using stageEndsAt if provided
    let endTarget = null;
    if (currentMatch?.endTime) {
      endTarget = new Date(currentMatch.endTime).getTime();
    } else if (stageEndsAt) {
      endTarget = Number(stageEndsAt);
    }

    if (!endTarget) {
      // Fallback to provided timeLeft field if any
      const t = currentMatch?.timeLeft || 0;
      setTimeLeft(Math.max(0, Number(t) || 0));
      return;
    }

    let timerId;
    const tick = () => {
      const diff = Math.max(0, Math.floor((endTarget - Date.now()) / 1000));
      setTimeLeft(diff);
      if (diff === 0) {
        if (currentMatch?.status === 'in-session') {
          endedViaTimerRef.current = true;
          if (currentMatch?._id && userId) {
            endSession(currentMatch._id, userId, sessionId);
          }
        }
        clearInterval(timerId);
      }
    };

    tick();
    timerId = setInterval(tick, 1000);
    return () => clearInterval(timerId);
  }, [
    currentMatch?._id,
    currentMatch?.endTime,
    currentMatch?.status,
    currentMatch?.timeLeft,
    endSession,
    stageEndsAt,
    userId,
    sessionId,
  ]);

  /* ───────── Redirect home when completed ───────── */
  useEffect(() => {
    if (!currentMatch || redirectedRef.current) return;
    if (currentMatch.status !== 'completed') return;

    const eId = localStorage.getItem('mobileEventId');
    const target = eId ? `/matchflow?eventId=${eId}` : '/matchflow';

    let msg = 'Session ended.';
    let tone = 'success';

    if (endedViaTimerRef.current) {
      msg = "Time's up. Thanks for chatting!";
      tone = 'success';
    } else if (currentMatch?.enderId && userId) {
      if (String(currentMatch.enderId) === String(userId)) {
        msg = 'You ended the match early.';
        tone = 'error';
      } else {
        msg = 'Your partner ended the match early.';
        tone = 'error';
      }
    }

    addToast(msg, tone);
    redirectedRef.current = true;
    navigate(target, { replace: true });
  }, [currentMatch, userId, navigate, addToast]);

  function handleEndSession() {
    if (currentMatch?._id && userId) {
      endSession(currentMatch._id, userId, sessionId);
    }
  }

  // Format as MM:SS (or HH:MM:SS if >= 1 hour)
  const h = Math.floor(timeLeft / 3600);
  const m = Math.floor((timeLeft % 3600) / 60);
  const s = timeLeft % 60;
  const displayTime =
    h > 0
      ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

  const icebreakerQuestion = currentMatch?.icebreakerQuestion || '';

  return (
    <div style={PAGE.shell}>
      <EventProgressNavBar
        title="MatchFlow"
        leftLabel="End"
        onLeftClick={() => setShowEndSheet(true)}
        currentStep={4}
        totalSteps={5}
      />

      <EndMatchBottomSheet
        visible={showEndSheet}
        onCancel={() => setShowEndSheet(false)}
        onConfirm={() => {
          setShowEndSheet(false);
          handleEndSession();
        }}
        fromInSession
      />

      <div style={PAGE.center}>
        <TimerHeader timer={displayTime} />
        <IcebreakerCard
          question={icebreakerQuestion || 'Say hello and coordinate a place to meet.'}
        />
      </div>

      <MinimalFooter />
    </div>
  );
}

const PAGE = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: '#fff',
  },
  center: {
    flex: 1,
    maxWidth: 500,
    width: '100%',
    margin: '0 auto',
    textAlign: 'center',
  },
};
